<template>
  <a-modal
    :visible="props.visible"
    :title="$t('offers.create_new_attribute')"
    :width="700"
    centered
    :okText="$t('common.create')"
    :cancelText="$t('common.cancel')"
    :confirmLoading="isFetching"
    :closable="!isBusy"
    :maskClosable="!isBusy"
    :keyboard="!isBusy"
    :cancelButtonProps="{ props: { disabled: isBusy } }"
    :okButtonProps="{ props: { disabled: !!translatingField } }"
    @cancel="onClose"
    @ok="onSubmit"
  >
    <a-form-model
      ref="formRef"
      :model="form"
      :rules="formRules"
      :colon="false"
      layout="vertical"
      class="new-attribute-form"
    >
      <section>
        <a-divider
          orientation="left"
          dashed
        >
          {{ $t("offers.create_form_fields.attribute_name") }}
        </a-divider>
        <a-form-model-item
          label="UA"
          prop="public_name_mlang.uk"
          v-bind="getFieldErrorProps('public_name_mlang.uk')"
        >
          <div class="new-attribute-form__row">
            <a-input
              v-model.trim="form.public_name_mlang.uk"
              @input="clearServerError('public_name_mlang.uk')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.public_name_mlang.uk"
              :loading="translatingField === 'public_name_mlang.uk'"
              @click="translateField('public_name_mlang', 'uk')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
        <a-form-model-item
          label="RU"
          prop="public_name_mlang.ru"
          v-bind="getFieldErrorProps('public_name_mlang.ru')"
        >
          <div class="new-attribute-form__row">
            <a-input
              v-model.trim="form.public_name_mlang.ru"
              @input="clearServerError('public_name_mlang.ru')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.public_name_mlang.ru"
              :loading="translatingField === 'public_name_mlang.ru'"
              @click="translateField('public_name_mlang', 'ru')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
      </section>
      <section>
        <a-divider
          orientation="left"
          dashed
        >
          {{ $t("offers.create_form_fields.attribute_value") }}
        </a-divider>
        <a-form-model-item
          label="UA"
          prop="value_mlang.uk"
          v-bind="getFieldErrorProps('value_mlang.uk')"
        >
          <div class="new-attribute-form__row">
            <a-input
              v-model.trim="form.value_mlang.uk"
              @input="clearServerError('value_mlang.uk')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.value_mlang.uk"
              :loading="translatingField === 'value_mlang.uk'"
              @click="translateField('value_mlang', 'uk')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
        <a-form-model-item
          label="RU"
          prop="value_mlang.ru"
          v-bind="getFieldErrorProps('value_mlang.ru')"
        >
          <div class="new-attribute-form__row">
            <a-input
              v-model.trim="form.value_mlang.ru"
              @input="clearServerError('value_mlang.ru')"
              :disabled="isBusy"
            />
            <a-button
              :disabled="isBusy || !form.value_mlang.ru"
              :loading="translatingField === 'value_mlang.ru'"
              @click="translateField('value_mlang', 'ru')"
            >
              <a-icon
                type="swap"
                :rotate="90"
              />
              {{ $t("offers.translate") }}
            </a-button>
          </div>
        </a-form-model-item>
      </section>
    </a-form-model>
  </a-modal>
</template>

<script setup lang="ts">
import type { FormModel } from "ant-design-vue"
import { message } from "ant-design-vue"
import { computed, reactive, ref, shallowRef } from "vue"

import useForm from "@/composables/useForm"
import { useServerFormErrors } from "@/composables/useServerFormErrors"
import i18n from "@/i18n"
import { translateText } from "@/modules/offers/api/productModeration"
import type {
  AttributeFormLocale,
  CreateAttributeFormValues
} from "@/modules/offers/types/createOffer"
import type { ProductModerationAttribute } from "@/modules/offers/types/offers"
import type { CreateAttributeResponse } from "@/modules/offers/types/productModeration"

import { findOrCreateAttribute } from "../../utils/findOrCreateAttribute"
import { findOrCreateAttributeValue } from "../../utils/findOrCreateAttributeValue"

type Props = {
  visible?: boolean
}
type Emits = {
  (event: "submit", payload: ProductModerationAttribute): void
  (event: "close"): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isFetching = shallowRef(false)
const translatingField = ref<string>()

const isBusy = computed(() => isFetching.value || !!translatingField.value)

const formRef = ref<FormModel>()
const form = reactive<CreateAttributeFormValues>({
  public_name_mlang: { uk: "", ru: "" },
  value_mlang: { uk: "", ru: "" }
})

const { validateForm, isValidForm, resetFields } = useForm(formRef)
const { getFieldErrorProps, setServerErrors, clearServerError, clearServerErrors } =
  useServerFormErrors()

const formRules = computed(() => {
  const required = [
    {
      required: true,
      whitespace: true,
      message: i18n.t("offers.product_moderation_errors.required") as string
    }
  ]
  return {
    "public_name_mlang.uk": required,
    "public_name_mlang.ru": required,
    "value_mlang.uk": required,
    "value_mlang.ru": required
  }
})

const translateField = async (
  field: keyof CreateAttributeFormValues,
  locale: AttributeFormLocale
) => {
  if (isBusy.value) return

  const text = form[field][locale]

  if (!text) return

  const targetLocale = locale === "uk" ? "ru" : "uk"
  translatingField.value = `${field}.${locale}`
  try {
    const { data } = await translateText({ text, target_locale: targetLocale })
    form[field][targetLocale] = data.text.trim()
    clearServerError(`${field}.${targetLocale}`)
    formRef.value.clearValidate(`${field}.${targetLocale}`)
  } catch (error) {
    setServerErrors(error, { text: [`${field}.${locale}`] })
  } finally {
    translatingField.value = undefined
  }
}

let resolvedAttribute: CreateAttributeResponse | undefined
let resolvedAttributeName = ""

const onSubmit = async () => {
  if (isBusy.value) return

  clearServerErrors()
  isFetching.value = true
  try {
    await validateForm()

    if (!isValidForm.value) {
      message.error(i18n.t("offers.offer_form_validate_error") as string)
      return
    }

    if (!resolvedAttribute || resolvedAttributeName !== form.public_name_mlang.uk) {
      resolvedAttribute = await findOrCreateAttribute({ ...form.public_name_mlang })
      resolvedAttributeName = form.public_name_mlang.uk
    }

    const valueData = await findOrCreateAttributeValue({
      attributeCode: resolvedAttribute.code,
      attributeValueType: "STRING",
      formValues: { value_mlang: { ...form.value_mlang } }
    })

    emit("submit", { ...resolvedAttribute, value: [valueData] })
    onClose()
  } catch (error) {
    const nameFields = ["public_name_mlang.uk", "public_name_mlang.ru"]
    const valueFields = ["value_mlang.uk", "value_mlang.ru"]
    setServerErrors(error, {
      name: nameFields,
      public_name: nameFields,
      public_name_mlang: nameFields,
      value: valueFields,
      value_mlang: valueFields
    })
  } finally {
    isFetching.value = false
  }
}
const onClose = () => {
  emit("close")
  resetFields()
  clearServerErrors()
  resolvedAttribute = undefined
  resolvedAttributeName = ""
}
</script>

<style lang="scss" scoped>
.new-attribute-form {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-divider {
    margin: 0 0 10px;
    font-size: 14px;
    line-height: 24px;
  }

  ::v-deep .ant-form-item-label {
    line-height: 22px;
  }

  .ant-form-item {
    margin-bottom: 10px;
    padding-bottom: 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__row {
    display: flex;
    gap: 16px;

    .ant-input {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
